import * as types from '../../actions/ActionTypes.js';
import DateParser from 'utils/date/DateParser.js';
import DateFormatter from 'utils/date/DateFormatter.js';

const INITIAL_STATE = {
    calendarSnackbar: '',
    showNavSidebar: true,
    showAddSidebar: false,
    addSidebarContent: {},
    refreshCalendar: null,
    preferences: null,
    isFetchingPreferences: false,
    year: null,
    month: null,
    date: null,
    calendarWeek: {
        isFetching: false,
        // we can have multiple group requests that will update the same state.
        // We use this to keep track of how many requests were sent, but not finished,
        // such that we can know when all requests have finished and render the page
        activeRequests: 0,
        data: null,
        isEcgFetching: false,
        ecgData: null
    },
    calendarMonth: {
        isFetching: false,
        activeRequests: 0,
        data: null,
        isEcgFetching: false,
        ecgData: null
    },
    calendarYear: {
        isFetching: false,
        data: null,
        isEcgFetching: false,
        ecgData: null
    },
    notes: {
        data: {},
        activeRequests: 0,
        isFetching: false
    },
    event: {
        data: null,
        isFetching: false
    },
    primaryEvent: {
        data: null,
        isFetching: false
    },
    modalDispatcher: {},
    addMenu: {
        date: null
    },
    export: {
        isFetching: false,
        data: null
    },
    groupCalendar: false,
    showCalendarMessage: false,
    calendarSentToDeviceSuccess: false,
    calendarSentToDeviceError: false,
    upcomingEventsFetching: true,
    upcomingEventsList: []
};

const requestCalendarPreferences = (state) => {
    return {
        ...state,
        isFetchingPreferences: true
    };
};

const receiveCalendarPreferences = (state, action) => {
    return {
        ...state,
        showNavSidebar: action.calendarPreferences.showOptions,
        preferences: { ...action.calendarPreferences },
        isFetchingPreferences: false
    };
};

const showSnackbar = (state, action) => ({
    ...state,
    calendarSnackbar: action.message
});

const showAddSidebar = (state, action) => ({
    ...state,
    showNavSidebar: false,
    showAddSidebar: true,
    addSidebarContent: {
        content: action.content,
        date: action.date,
        editMode: action.editMode,
        data: action.data
    },
    addMenu: INITIAL_STATE.addMenu
});

const hideAddSidebar = (state) => ({
    ...state,
    showNavSidebar: state.preferences.showOptions,
    showAddSidebar: INITIAL_STATE.showAddSidebar,
    addSidebarContent: INITIAL_STATE.addSidebarContent,
    addMenu: INITIAL_STATE.addMenu
});

const refreshCalendar = (state, action) => {
    return {
        ...state,
        refreshCalendar: {
            date: action.date
        }
    };
};

const setCalendarYearMonthDate = (state, action) => {
    return {
        ...state,
        year: action.year || state.year,
        month: !isNaN(action.month) ? action.month : state.month,
        date: action.date || state.date,
        addMenu: INITIAL_STATE.addMenu
    };
};

const requestCalendarWeek = (state, action) => {
    const data = action.append ? { ...state.calendarWeek } : { ...INITIAL_STATE.calendarWeek };

    return {
        ...state,
        calendarWeek: {
            ...data,
            activeRequests: action.append ? state.calendarWeek.activeRequests : state.calendarWeek.activeRequests + 1,
            isFetching: !action.append,
            ecgData: state.calendarWeek.ecgData
        }
    };
};

const receiveCalendarWeek = (state, action) => {
    const items = action.calendarWeek ? action.calendarWeek.calendarItems : {};

    return {
        ...state,
        calendarWeek: {
            ...state.calendarWeek,
            activeRequests: action.append ? state.calendarWeek.activeRequests : state.calendarWeek.activeRequests - 1,
            // we check if there are more than 1 ongoing request instead of 0, because the current state at this point has not yet been decremented
            // The new state will simultaneously receive activeRequests: 0 and fetching: false
            isFetching: state.calendarWeek.activeRequests > 1,
            data: !state.calendarWeek.data
                ? action.calendarWeek
                : {
                      ...state.calendarWeek.data,
                      calendarItems: [...state.calendarWeek.data.calendarItems, ...items]
                  },
            lastUpdated: action.receivedAt
        }
    };
};

const requestCalendarMonth = (state, action) => {
    const data = action.append ? { ...state.calendarMonth } : { ...INITIAL_STATE.calendarMonth };

    return {
        ...state,
        calendarMonth: {
            ...data,
            activeRequests: action.append ? state.calendarMonth.activeRequests : state.calendarMonth.activeRequests + 1,
            isFetching: !action.append,
            ecgData: state.calendarMonth.ecgData
        }
    };
};

const receiveCalendarMonth = (state, action) => {
    const items = action.calendarMonth ? action.calendarMonth.calendarItems : {};

    return {
        ...state,
        calendarMonth: {
            ...state.calendarMonth,
            activeRequests: action.append ? state.calendarMonth.activeRequests : state.calendarMonth.activeRequests - 1,
            isFetching: state.calendarMonth.activeRequests > 1,
            data: !state.calendarMonth.data
                ? action.calendarMonth
                : {
                      ...state.calendarMonth.data,
                      calendarItems: [...state.calendarMonth.data.calendarItems, ...items]
                  },
            lastUpdated: action.receivedAt
        }
    };
};

const requestCalendarYear = (state) => ({
    ...state,
    calendarYear: {
        ...INITIAL_STATE.calendarYear,
        isFetching: true,
        ecgData: state.calendarYear.ecgData
    }
});

const receiveCalendarYear = (state, action) => ({
    ...state,
    calendarYear: {
        ...state.calendarYear,
        isFetching: false,
        data: action.calendarYear,
        lastUpdated: action.receivedAt
    }
});

const setModalDispatcher = (state, action) => ({
    ...state,
    modalDispatcher: {
        modal: action.modal,
        data: action.data,
        date: action.date,
        trainingPlanId: action.trainingPlanId
    },
    addMenu: INITIAL_STATE.addMenu
});

const resetModalDispatcher = (state) => ({
    ...state,
    modalDispatcher: INITIAL_STATE.modalDispatcher,
    notes: INITIAL_STATE.notes,
    event: INITIAL_STATE.event,
    addMenu: INITIAL_STATE.addMenu
});

const requestCalendarNote = (state) => {
    const data = state.notes.activeRequests === 0 ? INITIAL_STATE.notes.data : state.notes.data;

    return {
        ...state,
        notes: {
            data: {
                ...data
            },
            isFetching: true,
            activeRequests: state.notes.activeRequests + 1
        }
    };
};

const receiveCalendarNote = (state, action) => ({
    ...state,
    notes: {
        data: {
            ...state.notes.data,
            [action.noteId]: action.json
        },
        isFetching: state.notes.activeRequests > 1,
        activeRequests: state.notes.activeRequests - 1
    }
});

const requestCalendarEvent = (state) => {
    return {
        ...state,
        event: {
            ...INITIAL_STATE.event,
            isFetching: true
        }
    };
};

const receiveCalendarEvent = (state, action) => {
    return {
        ...state,
        event: {
            data: action.json,
            isFetching: false
        }
    };
};

const showAddMenu = (state, action) => ({
    ...state,
    addMenu: {
        date: action.date,
        clickTop: action.clickTop,
        clickLeft: action.clickLeft
    }
});

const hideAddMenu = (state) => ({
    ...state,
    addMenu: INITIAL_STATE.addMenu
});

const updateCalendarItems = (state, action) => {
    return {
        ...state,
        [action.view]: {
            ...state[action.view],
            data: {
                ...state[action.view].data,
                calendarItems: action.items
            }
        }
    };
};

const requestCalendarExport = (state) => {
    return {
        ...state,
        export: {
            ...INITIAL_STATE.export,
            isFetching: true
        }
    };
};

const receiveCalendarExport = (state, action) => {
    return {
        ...state,
        export: {
            data: action.json,
            isFetching: false
        }
    };
};

const displayCalendarMessage = (state, action) => {
    return {
        ...state,
        showCalendarMessage: action.show
    };
};

const calendarItemsSentToDeviceSuccess = (state) => {
    return {
        ...state,
        calendarSentToDeviceSuccess: true
    };
};

const calendarItemsSentToDeviceError = (state) => {
    return {
        ...state,
        calendarSentToDeviceError: true
    };
};

const resetCalendarItemsSentToDevice = (state) => {
    return {
        ...state,
        calendarSentToDeviceSuccess: false,
        calendarSentToDeviceError: false
    };
};

const resetCalendarPage = () => {
    return INITIAL_STATE;
};

const ecgRecordingsForCalendarFetching = (state, action) => {
    switch (action.periodType) {
        case 'week':
            return {
                ...state,
                calendarWeek: {
                    ...state.calendarWeek,
                    isEcgFetching: true
                }
            };
        case 'month':
            return {
                ...state,
                calendarMonth: {
                    ...state.calendarMonth,
                    isEcgFetching: true
                }
            };
        default:
            return {
                ...state,
                calendarYear: {
                    ...state.calendarYear,
                    isEcgFetching: true
                }
            };
    }
};

const ecgRecordingsForCalendarFinished = (isSuccess) => (state, action) => {
    const actionData =
        isSuccess && action.data
            ? action.data.map((ecgR) => ({
                  ...ecgR,
                  itemType: 'ecg',
                  date: DateFormatter.formatISODate(DateParser.parseISO(ecgR.startTimeLocal))
              }))
            : [];
    switch (action.periodType) {
        case 'week':
            return {
                ...state,
                calendarWeek: {
                    ...state.calendarWeek,
                    isEcgFetching: false,
                    ecgData: actionData
                }
            };
        case 'month':
            return {
                ...state,
                calendarMonth: {
                    ...state.calendarMonth,
                    isEcgFetching: false,
                    ecgData: actionData
                }
            };
        default:
            return {
                ...state,
                calendarYear: {
                    ...state.calendarYear,
                    isEcgFetching: false,
                    ecgData: actionData
                }
            };
    }
};

const ecgRecordingDeleteFinished = (isSuccess) => (state, action) => {
    if (!isSuccess) {
        return state;
    }

    // need to delete the ECG recording from all state instances
    let ecgIndex = -1;
    const ecgId = action.payload;

    const weekState = { ...state.calendarWeek };
    if (weekState.ecgData) {
        ecgIndex = weekState.ecgData.findIndex((ecg) => ecg.detailId === ecgId);
        if (ecgIndex >= 0) {
            weekState.ecgData.splice(ecgIndex, 1);
        }
    }

    const monthState = { ...state.calendarMonth };
    if (monthState.ecgData) {
        ecgIndex = monthState.ecgData.findIndex((ecg) => ecg.detailId === ecgId);
        if (ecgIndex >= 0) {
            monthState.ecgData.splice(ecgIndex, 1);
        }
    }

    const yearState = { ...state.calendarYear };
    if (yearState.ecgData) {
        ecgIndex = yearState.ecgData.findIndex((ecg) => ecg.detailId === ecgId);
        if (ecgIndex >= 0) {
            yearState.ecgData.splice(ecgIndex, 1);
        }
    }

    return {
        ...state,
        calendarWeek: weekState,
        calendarMonth: monthState,
        calendarYear: yearState
    };
};

const requestPrimaryEventMetadata = (state) => {
    return {
        ...state,
        primaryEvent: {
            ...INITIAL_STATE.primaryEvent,
            isFetching: true
        }
    };
};

const receivePrimaryEventMetadata = (state, action) => {
    return {
        ...state,
        primaryEvent: {
            data: action.json,
            isFetching: false
        }
    };
};

const requestingUpcomingEvents = (state) => {
    return {
        ...state,
        upcomingEventsFetching: true
    };
};

const receivedUpcomingEvents = (state, action) => {
    return {
        ...state,
        upcomingEventsFetching: false
    };
};

const handlers = {
    [types.REQUEST_CALENDAR_PREFERENCES]: requestCalendarPreferences,
    [types.RECEIVE_CALENDAR_PREFERENCES]: receiveCalendarPreferences,
    [types.SET_CALENDAR_YEAR_AND_MONTH]: setCalendarYearMonthDate,
    [types.REQUEST_CALENDAR_WEEK]: requestCalendarWeek,
    [types.RECEIVE_CALENDAR_WEEK]: receiveCalendarWeek,
    [types.REQUEST_CALENDAR_MONTH]: requestCalendarMonth,
    [types.RECEIVE_CALENDAR_MONTH]: receiveCalendarMonth,
    [types.REQUEST_CALENDAR_YEAR]: requestCalendarYear,
    [types.RECEIVE_CALENDAR_YEAR]: receiveCalendarYear,
    [types.SHOW_ADD_SIDEBAR]: showAddSidebar,
    [types.HIDE_ADD_SIDEBAR]: hideAddSidebar,
    [types.SET_MODAL_DISPATCHER]: setModalDispatcher,
    [types.RESET_MODAL_DISPATCHER]: resetModalDispatcher,
    [types.SET_CALENDAR_SNACKBAR]: showSnackbar,
    [types.SHOW_ADD_MENU]: showAddMenu,
    [types.HIDE_ADD_MENU]: hideAddMenu,
    [types.REQUEST_CALENDAR_NOTE]: requestCalendarNote,
    [types.RECEIVE_CALENDAR_NOTE]: receiveCalendarNote,
    [types.REQUEST_CALENDAR_EVENT]: requestCalendarEvent,
    [types.RECEIVE_CALENDAR_EVENT]: receiveCalendarEvent,
    [types.UPDATE_CALENDAR_ITEMS]: updateCalendarItems,
    [types.REQUEST_CALENDAR_EXPORT]: requestCalendarExport,
    [types.RECEIVE_CALENDAR_EXPORT]: receiveCalendarExport,
    [types.RESET_CALENDAR_PAGE]: resetCalendarPage,
    [types.DISPLAY_CALENDAR_MESSAGE]: displayCalendarMessage,
    [types.SEND_CALENDAR_ITEMS_TO_DEVICE_SUCCESS]: calendarItemsSentToDeviceSuccess,
    [types.RESET_CALENDAR_ITMES_TO_DEVICE]: resetCalendarItemsSentToDevice,
    [types.SEND_CALENDAR_ITEMS_TO_DEVICE_ERROR]: calendarItemsSentToDeviceError,
    [types.REFRESH_CALENDAR]: refreshCalendar,
    [types.FETCH_ECG_RECORDINGS_FOR_CALENDAR_START]: ecgRecordingsForCalendarFetching,
    [types.FETCH_ECG_RECORDINGS_FOR_CALENDAR_SUCCESS]: ecgRecordingsForCalendarFinished(true),
    [types.FETCH_ECG_RECORDINGS_FOR_CALENDAR_FAIL]: ecgRecordingsForCalendarFinished(false),
    [types.DELETE_ECG_RECORDING_SUCCESS]: ecgRecordingDeleteFinished(true),
    [types.DELETE_ECG_RECORDING_FAIL]: ecgRecordingDeleteFinished(false),
    [types.RECEIVE_PRIMARY_EVENT_METADATA]: receivePrimaryEventMetadata,
    [types.REQUEST_PRIMARY_EVENT_METADATA]: requestPrimaryEventMetadata,
    [types.RECEIVE_CALENDAR_UPCOMING_EVENTS]: receivedUpcomingEvents,
    [types.REQUEST_CALENDAR_UPCOMING_EVENTS]: requestingUpcomingEvents
};

export const getCalendarPage = (state, groupId) => {
    state.calendarPage.groupCalendar = !!groupId;
    return state.calendarPage;
};

export const isCalendarItemsSentToDeviceSuccess = (state) => {
    return state.calendarPage.calendarSentToDeviceSuccess === true;
};

export const isCalendarItemsSentToDeviceError = (state) => {
    return state.calendarPage.calendarSentToDeviceError === true;
};

export const isDisplayCalendarMessage = (state) => {
    return state.calendarPage.showCalendarMessage === true;
};

export const getCalendarEvent = (state) => {
    return state.event.data;
};

export const getEventMetadata = (state) => {
    return state.calendarPage.primaryEvent.data;
};

export const getCalendarSidebarContent = (state) => state.calendarPage.addSidebarContent;

export const getCalendarPreferences = (state) => state.calendarPage.preferences;

export const upcomingEventsIsFetching = (state) => state.calendarPage.upcomingEventsFetching;

export default function calendarPage(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}
